import React, { useEffect } from 'react';

import { track } from '@betterment/js-runtime';
import { Box, Icon, Text } from '@betterment/samba-react';
import { SambaAutoLink } from '@betterment/shared-components';
import { CenteredWorkflowLayout, Heading } from '@betterment/style-closet-react';

import { trackClick } from '../../features/track';
import Footer from '../../Shared/components/Footer';
import { MFA_LOCKED_SUPPORT_PHONE } from '../../Shared/constants';
import { urlBuilder } from '../../Shared/urlBuilder';
import { Account, Customer } from '../data';
import { isCustomerLocked as isCardCustomerLocked, isLocked } from '../models/card';

export interface Props {
  customer: Customer;
  title: string;
  accountId: Account['id'];
}

const LockedError: React.FC<React.PropsWithChildren<Props>> = ({ title, customer, accountId }) => {
  const isCustomerLocked = customer.isLocked;
  const isCardOnlyLocked = isLocked(customer.card) && !isCustomerLocked;
  const shouldShowCustomerLockedCta = isCardOnlyLocked && isCardCustomerLocked(customer.card);
  useEffect(() => {
    track('ElementViewed', { name: 'CoreproLock', flow: 'OpenJointCheckingSecondary' });
  }, []);

  let hrefValue = urlBuilder.appHome.absolute();
  if (shouldShowCustomerLockedCta) {
    hrefValue = urlBuilder.getAppCheckingSummary.absolute({ accountId });
  }
  const buttonCopy = shouldShowCustomerLockedCta ? 'Go to settings' : 'Got it';

  return (
    <CenteredWorkflowLayout title={title}>
      <CenteredWorkflowLayout.Slot name="content">
        <>
          <Box
            css={{
              marginBottom: 'space.300',
            }}
          >
            <Icon name="ExclamationCircle" size="large" color="icon" />
          </Box>
          {isCustomerLocked ? (
            <Heading as="h1" fontSize={[5, 6]} mb="8">
              Your existing account with us is locked
            </Heading>
          ) : (
            <Heading as="h1" fontSize={[5, 6]} mb="8">
              Your debit card is currently locked
            </Heading>
          )}
          <Box
            css={{
              marginBottom: 'space.400',
            }}
          >
            {isCustomerLocked ? (
              <Text as="p">
                Please call us at <a href={`tel:${MFA_LOCKED_SUPPORT_PHONE}`}>{MFA_LOCKED_SUPPORT_PHONE}</a> so we can
                assist you.
              </Text>
            ) : (
              <Text as="p">
                You'll need to unlock your Betterment Visa&reg; Debit Card before you can create your new Checking
                account.
              </Text>
            )}
          </Box>
        </>
        <CenteredWorkflowLayout.Actions>
          <SambaAutoLink
            onClick={() => trackClick({ name: 'GotIt', flow: 'OpenJointCheckingSecondary' })}
            to={hrefValue}
            variant="solidPrimary"
          >
            {buttonCopy}
          </SambaAutoLink>
        </CenteredWorkflowLayout.Actions>
      </CenteredWorkflowLayout.Slot>
      <CenteredWorkflowLayout.Slot name="footer">
        <Footer />
      </CenteredWorkflowLayout.Slot>
    </CenteredWorkflowLayout>
  );
};
export default LockedError;
